/**
 * Aug-2026 (User-Wunsch) — Dispo-Zahnrad mit echter Funktion:
 * DMS-Zugangsdaten (e-Archivsystem / Pietsch) org-weit hinterlegen.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Loader2, Archive } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { invalidateDmsSettings, DEFAULT_DMS_BASE } from "../../lib/dms";

export default function DispoSettingsModal({ onClose }) {
    const [baseUrl, setBaseUrl] = useState("");
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/dispo/dms-settings").then((r) => {
            setBaseUrl(r.data?.base_url || "");
            setUsername(r.data?.username || "");
            setPassword(r.data?.password || "");
        }).catch(() => {});
    }, []);

    const save = async () => {
        setBusy(true);
        try {
            await api.put("/dispo/dms-settings", { base_url: baseUrl, username, password });
            invalidateDmsSettings();
            toast.success("DMS-Zugangsdaten gespeichert");
            onClose?.();
        } catch {
            toast.error("Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg rounded-sm" data-testid="dispo-settings-modal">
                <DialogHeader>
                    <DialogTitle className="font-display font-black uppercase tracking-wider flex items-center gap-2">
                        <Archive className="w-5 h-5 text-teal-700" /> DMS-Archiv (e-Archivsystem)
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Zugangsdaten für das Dokumenten-Archiv — gelten für alle Standorte. Der DMS-Button
                        in der Dispositionsliste öffnet das Archiv direkt mit der FIN des Fahrzeugs.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Archiv-URL</span>
                        <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                            placeholder={DEFAULT_DMS_BASE} className="rounded-sm mt-0.5"
                            data-testid="dms-base-url" />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Benutzername</span>
                            <Input value={username} onChange={(e) => setUsername(e.target.value)}
                                placeholder="DMS-Benutzer" className="rounded-sm mt-0.5" autoComplete="off"
                                data-testid="dms-username" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Passwort</span>
                            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••" className="rounded-sm mt-0.5" autoComplete="new-password"
                                data-testid="dms-password" />
                        </label>
                    </div>
                    <p className="text-[11px] text-gray-500 bg-teal-50 border border-teal-200 rounded-sm px-2.5 py-2">
                        ⓘ Die Pietsch-Login-Seite unterstützt technisch keinen URL-Auto-Login (ASP.NET-Formular).
                        Stattdessen: Beim Klick auf „DMS" wird das hinterlegte <b>Passwort automatisch in die
                        Zwischenablage kopiert</b> — auf der Login-Seite nur noch einfügen. Tipp: einmal im Browser
                        „Passwort merken" wählen, dann geht es künftig ganz ohne Tippen.
                    </p>
                    <Button onClick={save} disabled={busy}
                        className="w-full bg-[#111827] hover:bg-black text-white rounded-sm font-black uppercase tracking-wider"
                        data-testid="dms-settings-save">
                        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Speichern
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
